import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  InputSearch,
  TableData,
  Status,
  Pagination,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import { Column, Oem } from '@/types/common';
import {
  getListOem,
  createOEM,
  updateOEM,
  CreateOEMRequest,
  UpdateOEMRequest,
  downloadOemsCsv,
  settleOemIdleCost,
} from '@/services/api/oems';
import { UniversalFilter } from '@/components';
import { AddOEMModal, SettleIdleCostConfirmModal } from './components';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserEmail } from '@/utils';

interface OemFilters extends Record<string, any> {
  partnershipStatus?: ('ACTIVE' | 'INACTIVE')[];
}

interface OemRequestParams {
  page: string;
  size: string;
  search?: string;
  partnershipStatuses?: string;
}

// Settle/Resume Idle Cost row actions are hidden until ops is ready to use
// them (backend endpoint already live) — flip to true to show them again.
const SHOW_SETTLE_IDLE_COST_ACTIONS = false;

const OEMsPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [oemData, setOemData] = useState<Oem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState<OemFilters>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingOEM, setEditingOEM] = useState<Oem | null>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    null
  );
  const [pageSize, setPageSize] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);
  // OEM + direction for the settle-idle-cost confirm modal (null = closed)
  const [settleTarget, setSettleTarget] = useState<{
    oem: Oem;
    mode: 'settle' | 'resume';
  } | null>(null);
  const [isSettling, setIsSettling] = useState(false);

  const handleToggleDropdown = useCallback(
    (id: string | number) => {
      setActionDropdownOpen((prev) => (prev === id ? null : id));
    },
    []
  );

  useEffect(() => {
    const handleOutsideClick = () => setActionDropdownOpen(null);
    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleOutsideClick);
    }
    return () => document.removeEventListener('click', handleOutsideClick);
  }, [actionDropdownOpen]);

  // Filter configuration for UniversalFilter
  const filterConfigs = useMemo(
    () => [
      {
        key: 'partnershipStatus',
        title: 'OEM Partnership',
        icon: 'ri-building-2-line',
        multiple: true,
        staticOptions: [
          { key: 'active', label: 'Active', value: 'ACTIVE' },
          { key: 'inactive', label: 'Not Active', value: 'INACTIVE' },
        ],
      },
    ],
    []
  );

  const fetchOEMs = useCallback(
    async (
      page: number = 1,
      search?: string,
      filters?: OemFilters,
      size?: number
    ) => {
      setIsLoading(true);
      try {
        const params: OemRequestParams = {
          page: page.toString(),
          size: (size || pageSize).toString(),
        };

        if (search?.trim()) {
          params.search = search.trim();
        }

        if (
          filters?.partnershipStatus &&
          filters.partnershipStatus.length > 0
        ) {
          params.partnershipStatuses = filters.partnershipStatus.join(',');
        }

        const response = await getListOem(params);

        setOemData(response.data || []);
        setTotal(response.pagination?.total || 0);

        if (response.pagination?.page) {
          setCurrentPage(response.pagination.page);
        }
      } catch (error) {
        const errorMessage = extractApiErrorMessage(
          error,
          'Failed to load OEMs'
        );
        showErrorToast('Error', errorMessage);
        setOemData([]);
        setTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchOEMs(currentPage, searchTerm, activeFilters, pageSize);
  }, [fetchOEMs, currentPage, searchTerm, activeFilters, pageSize]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const sortedOemData = useMemo(() => {
    if (!sortField || !sortDirection) {
      return oemData;
    }

    return [...oemData].sort((a: Oem, b: Oem) => {
      const aValue = a[sortField as keyof Oem];
      const bValue = b[sortField as keyof Oem];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;
      if (aValue === bValue) return 0;

      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        // Convert to string for comparison if types don't match
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [oemData, sortField, sortDirection]);

  const handleEditOEM = (oem: Oem) => {
    setEditingOEM(oem);
    setIsAddModalOpen(true);
  };

  const handleConfirmSettle = async () => {
    if (!settleTarget?.oem.ID) return;
    setIsSettling(true);
    try {
      const response = await settleOemIdleCost(settleTarget.oem.ID, {
        settled: settleTarget.mode === 'settle',
        updatedBy: getUserEmail(),
      });
      const affected = response.data?.affected ?? 0;
      showSuccessToast(
        'Success',
        settleTarget.mode === 'settle'
          ? `${affected} unit(s) marked as settled — idle cost stopped`
          : `${affected} unit(s) resumed idle cost accrual`
      );
      setSettleTarget(null);
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to update idle cost settlement'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsSettling(false);
    }
  };

  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    const params: Record<string, any> = {};

    if (
      activeFilters?.partnershipStatus &&
      activeFilters.partnershipStatus.length > 0
    ) {
      params.partnershipStatuses = activeFilters.partnershipStatus.join(',');
    }

    try {
      const blob = await downloadOemsCsv(params);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dateStamp = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.download = `oems_${dateStamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccessToast('Success', 'Download started');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        'Failed to download OEMs'
      );
      showErrorToast('Error', errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const columns: Column[] = [
    {
      key: 'name',
      title: 'OEM Name',
      sortable: true,
      render: (value: string) => (
        <span className="font-medium text-[14px]">{value}</span>
      ),
    },
    {
      key: 'partnershipStatus',
      title: 'OEM Partnership',
      align: 'center',
      sortable: true,
      render: (value: string) => (
        <Status
          value={value === 'ACTIVE' ? 'active' : 'inactive'}
          type="feedback"
          size="medium"
        />
      ),
    },
    {
      key: 'dashboardLink',
      title: 'Dashboard Link',
      sortable: true,
      render: (value: string | null) =>
        value && value !== '-' ? (
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 underline text-[14px] cursor-pointer bg-transparent border-none p-0"
            onClick={(e) => {
              e.stopPropagation();
              const url = value.startsWith('http') ? value : `https://${value}`;
              window.open(url, '_blank', 'noopener,noreferrer');
            }}
            title={`Open ${value.startsWith('http') ? value : `https://${value}`
              } in new tab`}
          >
            {value.length > 30 ? `${value.substring(0, 30)}...` : value}
          </button>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      key: 'dashboardUsername',
      title: 'Dashboard Username',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'dashboardPassword',
      title: 'Dashboard Password',
      sortable: true,
      render: (value: string | null) => (
        <span className="text-[14px] font-normal">
          {value && value !== '-' ? value : <span className="text-gray-400">-</span>}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 'w-16',
      align: 'center',
      render: (_value: unknown, record: Oem, index: number) => {
        const uniqueId = record.ID || `temp-${index}`;
        const actions: ActionDropdownAction[] = [
          {
            key: 'edit',
            label: 'Edit',
            icon: 'ri-pencil-line',
            onClick: () => {
              handleEditOEM(record);
              setActionDropdownOpen(null);
            },
          },
          ...(SHOW_SETTLE_IDLE_COST_ACTIONS
            ? [
                {
                  key: 'settle-idle-cost',
                  label: 'Settle Idle Cost',
                  icon: 'ri-money-dollar-circle-line',
                  onClick: () => {
                    setSettleTarget({ oem: record, mode: 'settle' });
                    setActionDropdownOpen(null);
                  },
                },
                {
                  key: 'resume-idle-cost',
                  label: 'Resume Idle Cost',
                  icon: 'ri-refresh-line',
                  onClick: () => {
                    setSettleTarget({ oem: record, mode: 'resume' });
                    setActionDropdownOpen(null);
                  },
                },
              ]
            : []),
        ];
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={actions}
            ariaLabel={`Actions for OEM ${(record as any).name}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<Oem>({
    storageKey: 'oems',
    columns,
  });

  const handleSaveOEM = async (data: CreateOEMRequest) => {
    setIsCreating(true);
    try {
      const userEmail = getUserEmail();

      if (editingOEM) {
        const oemId = editingOEM?.ID;

        if (!oemId) {
          throw new Error('OEM ID is missing - cannot update');
        }

        const updatePayload: UpdateOEMRequest = {
          ...data,
          updatedBy: userEmail,
        };

        await updateOEM(oemId, updatePayload);
        showSuccessToast('Success', 'OEM updated successfully');
      } else {
        const createPayload: CreateOEMRequest = {
          ...data,
          createdBy: userEmail,
        };

        await createOEM(createPayload);
        showSuccessToast('Success', 'OEM created successfully');
      }

      setIsAddModalOpen(false);
      setEditingOEM(null);

      fetchOEMs(currentPage, searchTerm, activeFilters, pageSize);
    } catch (error) {
      const fallbackMessage = `Failed to ${editingOEM ? 'update' : 'create'
        } OEM`;
      const errorMessage = extractApiErrorMessage(error, fallbackMessage);
      showErrorToast('Error', errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              OEMs
            </h1>
            <p className="text-[14px] text-gray-600">
              Manage motorcycle manufacturers.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="white"
              size="small"
              onClick={handleDownloadCsv}
              disabled={isDownloading}
            >
              <i className="ri-download-line text-[12px] mr-1"></i>
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={() => setIsAddModalOpen(true)}
            >
              <i className="ri-add-line text-[12px] mr-1"></i>
              Create OEM
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-end gap-2">
          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />
          </div>

          <UniversalFilter
            filters={filterConfigs}
            currentFilters={activeFilters}
            onApply={(filters: OemFilters) => {
              setActiveFilters(filters);
              setCurrentPage(1);
            }}
            onClear={() => {
              setActiveFilters({});
              setCurrentPage(1);
            }}
          />

          <ColumnSettings {...columnSettingsProps} />
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading OEMs...</span>
            </div>
          ) : (
            <TableData
              data={sortedOemData}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1); // Reset to first page when changing page size
          fetchOEMs(1, searchTerm, activeFilters, newPageSize);
        }}
      />

      <AddOEMModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingOEM(null);
        }}
        onSubmit={handleSaveOEM}
        isLoading={isCreating}
        initialData={editingOEM}
        isEditMode={!!editingOEM}
      />

      <SettleIdleCostConfirmModal
        isOpen={settleTarget !== null}
        oemName={settleTarget?.oem.name ?? ''}
        mode={settleTarget?.mode ?? 'settle'}
        onClose={() => setSettleTarget(null)}
        onConfirm={handleConfirmSettle}
        isSubmitting={isSettling}
      />
    </div>
  );
};

export default OEMsPage;
